/**
 * PRISM versions are semantic versions (1.0.0-rc.1 from 1.0.0-rc.1): major.minor.patch with an optional
 * pre-release after a hyphen and build metadata after a plus. A pre-release comes before its release, so
 * 0.99.1 < 1.0.0-rc.1 < 1.0.0-rc.2 < 1.0.0 < 1.0.1. No dependencies: the agent downloads plugin of the web
 * dev server imports it on its own.
 */

/** major.minor.patch, an optional pre-release and optional build metadata. */
export const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;

export const isSemver = (v: string): boolean => SEMVER.test(v);

function parts(v: string): { core: number[]; pre: string[] } {
  const [main, ...rest] = v.trim().replace(/^v/, '').split('+')[0]!.split('-');
  const core = main!.split('.').map((x) => Number.parseInt(x, 10) || 0);
  const pre = rest.length ? rest.join('-').split('.') : [];
  return { core, pre };
}

/**
 * Negative when a is older than b, 0 when equal, positive when newer (SemVer 2.0.0 precedence: build metadata is
 * ignored; a pre-release is older than its release; pre-release identifiers compare numerically when both are
 * numbers, otherwise as text, and a number is older than text).
 */
export function compareVersions(a: string, b: string): number {
  const [x, y] = [parts(a), parts(b)];
  for (let i = 0; i < 3; i++) if ((x.core[i] ?? 0) !== (y.core[i] ?? 0)) return (x.core[i] ?? 0) - (y.core[i] ?? 0);
  if (!x.pre.length || !y.pre.length) return (x.pre.length ? -1 : 0) + (y.pre.length ? 1 : 0);
  for (let i = 0; i < Math.max(x.pre.length, y.pre.length); i++) {
    const [p, q] = [x.pre[i], y.pre[i]];
    if (p === undefined) return -1;
    if (q === undefined) return 1;
    if (p === q) continue;
    const [pn, qn] = [/^\d+$/.test(p), /^\d+$/.test(q)];
    if (pn && qn) return Number(p) - Number(q);
    if (pn !== qn) return pn ? -1 : 1;
    return p < q ? -1 : 1;
  }
  return 0;
}

/** True when `version` is older than `latest`. */
export const olderThan = (version: string, latest: string): boolean => compareVersions(version, latest) < 0;
